import React, { useEffect, useMemo, useState, memo } from 'react';
import {
  FormGroup,
  ControlLabel,
  Badge,
  FormControl,
  Collapse,
  InputGroup,
} from 'react-bootstrap';
import './_blog.form.scss'
import './_blog.form.dark-mode.scss'
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Translations from '../../../../../../components/Collapse/Translations';
import { getCurrentDomain, getErrorMessage } from '../../../../../../utils/helper';
import Summernote from '../../../../../../components/Summernote';
import Seo from '../../../../../../components/Seo';
import { summernoteOptions } from '../../helpers';
import PostImage from './PostImage';
import { pickDevParams } from '../../../../../../utils/config';
import { postImageDelete, postImageUpload } from '../../../../../../services/endpoints/blog';

const PostForm = ({ post, default_language, setErrorForm, blogUrl, isLoading, languages, isCreatePost, onChange }) => {
  const [formData, setFormData] = useState(post)
  const [isOpenTranslations, setOpenTranslations] = useState(false);
  const [isUploading, setUploading] = useState(false);

  const isVisibility = formData.visibility ? 1 : 0;
  const domain = useMemo(() => getCurrentDomain(), []);
  const defaultLanguage = useMemo(() => languages.find((language) => language.code === default_language), [default_language, languages])

  const anotherLanguages = useMemo(() => languages.filter((language) => language.code !== default_language), [default_language, languages])
  const clearBlogUrl = useMemo(() => blogUrl.replace('/', ''), [blogUrl]);

  const onChangeUrl = (value) => {
    const url = value
      .toLowerCase()
      .trim()
      .replace(/\s|_+/g, '')
    setFormData((prevState) => ({
      ...prevState,
      url,
    }))
  }

  const onChangeTitle = (value, languageCode) => {
    setFormData((prevState) => ({
      ...prevState,
      title: {
        ...prevState.title,
        [languageCode]: value,
      },
    }))
    /* Если создаем страницу, автоматически создаем url из названия */
    if (languageCode === default_language && isCreatePost) {
      onChangeUrl(value)
    }
  }

  const onChangeSeo = (value, languageCode) => {
    setFormData((prevState) => ({
      ...prevState,
      seo_title: {
        ...prevState.seo_title,
        [languageCode]: value.title,
      },
      seo_keywords: {
        ...prevState.seo_keywords,
        [languageCode]: value.keywords,
      },
      seo_description: {
        ...prevState.seo_description,
        [languageCode]: value.description,
      },
    }))
  }

  const onChangeVisibility = (value) => {
    const valueToNumber = Number(value);
    setFormData((prevState) => ({
      ...prevState,
      visibility: !!valueToNumber,
    }))
  }

  const onChangeBody = (value, languageCode) => {
    setFormData((prevState) => ({
      ...prevState,
      body: {
        ...prevState.body,
        [languageCode]: value,
      },
    }))
  }

  const onUploadFile = async (e) => {
    setUploading(true)
    const formData = new FormData();
    for (let i = 0; i < e.target.files.length; i++) {
      formData.append('image', e.target.files[i]);
    }
    Object.keys(pickDevParams).forEach((param) => {
      const value = pickDevParams[param];
      formData.append(param, value);
    })
    try {
      const response = await postImageUpload(formData);
      setErrorForm(null)
      setFormData((prevState) => ({
        ...prevState,
        image: response.data,
      }))
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setErrorForm(errorMessage);
    } finally {
      setUploading(false)
    }
  }

  const onDeleteFile = async () => {
    setUploading(true)
    try {
      await postImageDelete(formData.image.id);
      setErrorForm(null);
      setFormData((prevState) => ({
        ...prevState,
        image: null,
      }))
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setErrorForm(errorMessage);
    } finally {
      setUploading(false)
    }
  }

  useEffect(() => {
    onChange(formData)
  }, [formData, onChange])

  return (
    <div className="post-form">
      {isLoading ? <div className="modal-loader" /> : null}
      <PostImage
        isLoading={isUploading}
        file={formData.image}
        title="Post image"
        onChange={onUploadFile}
        onDelete={onDeleteFile}
        description="800 x 450px recommended"
      />
      <FormGroup
        className={classNames({
          'form-group__rtl': defaultLanguage.direction === 'rtl',
        })}
      >
        <ControlLabel htmlFor={`title-${defaultLanguage.code}`}>
          Post title
          {' '}
          <Badge>{defaultLanguage.title}</Badge>
        </ControlLabel>
        <Translations onOpen={() => setOpenTranslations(!isOpenTranslations)} open={isOpenTranslations} items={languages}>
          <FormControl
            type="text"
            id={`title-${defaultLanguage.code}`}
            name={`title_${defaultLanguage.code}`}
            value={formData.title[defaultLanguage.code]}
            onChange={(e) => onChangeTitle(e.target.value, defaultLanguage.code)}
            required
          />
        </Translations>
      </FormGroup>
      <FormGroup
        key={`content-${defaultLanguage.code}`}
        className={classNames({
          'form-group__rtl': defaultLanguage.direction === 'rtl',
        })}
      >
        <ControlLabel>
          Body
          {' '}
          <Badge>{defaultLanguage.title}</Badge>
        </ControlLabel>
        <Summernote
          value={formData.body[defaultLanguage.code]}
          options={summernoteOptions}
          onChange={(value) => onChangeBody(value, defaultLanguage.code)}
        />
      </FormGroup>
      <FormGroup>
        <Seo
          title={formData.seo_title[defaultLanguage.code]}
          keywords={formData.seo_keywords[defaultLanguage.code]}
          description={formData.seo_description[defaultLanguage.code]}
          languageName={defaultLanguage.title}
          url={`${clearBlogUrl}/${formData.url}`}
          onChange={(value) => onChangeSeo(value, defaultLanguage.code)}
        />
      </FormGroup>
      <Collapse in={isOpenTranslations}>
        <div>
          {anotherLanguages.length ? anotherLanguages.map((language) => (
            <div key={`title-translation-${language.code}`}>
              <FormGroup
                className={classNames({
                  'form-group__rtl': language.direction === 'rtl',
                })}
              >
                <ControlLabel htmlFor={`title-${language.code}`}>
                  Post title
                  {' '}
                  <Badge>{language.title}</Badge>
                </ControlLabel>
                <FormControl
                  type="text"
                  id={`title-${language.code}`}
                  name={`title_${language.code}`}
                  value={formData.title[language.code]}
                  onChange={(e) => onChangeTitle(e.target.value, language.code)}
                  required
                />
              </FormGroup>
              <FormGroup
                key={`content-${language.code}`}
                className={classNames({
                  'form-group__rtl': language.direction === 'rtl',
                })}
              >
                <ControlLabel>
                  Body
                  {' '}
                  <Badge>{language.title}</Badge>
                </ControlLabel>
                <Summernote
                  value={formData.body[language.code]}
                  options={summernoteOptions}
                  onChange={(value) => onChangeBody(value, language.code)}
                />
              </FormGroup>
              <FormGroup>
                <Seo
                  title={formData.seo_title[language.code]}
                  keywords={formData.seo_keywords[language.code]}
                  description={formData.seo_description[language.code]}
                  languageCode={language.code}
                  languageName={language.title}
                  url={`${clearBlogUrl}/${formData.url}`}
                  onChange={(value) => onChangeSeo(value, language.code)}
                />
              </FormGroup>
            </div>
          )) : null}
        </div>
      </Collapse>
      <>
        <div className="post-form__line" />
        <FormGroup>
          <ControlLabel htmlFor={`title-${defaultLanguage.code}`}>
            URL
          </ControlLabel>
          <InputGroup>
            <InputGroup.Addon>
              {`${domain}/${clearBlogUrl}`}
              /
            </InputGroup.Addon>
            <FormControl
              type="text"
              id="url"
              name="url"
              placeholder={formData.default_url || ''}
              value={formData.url}
              onChange={(e) => onChangeUrl(e.target.value)}
            />
          </InputGroup>
        </FormGroup>
      </>
      <FormGroup controlId="visibility">
        <ControlLabel>Visibility</ControlLabel>
        <FormControl
          value={isVisibility}
          onChange={(e) => onChangeVisibility(e.target.value)}
          componentClass="select"
          placeholder="select"
        >
          <option value="1">Published</option>
          <option value="0">Not published</option>
        </FormControl>
      </FormGroup>
    </div>
  );
};

const arePropsEqual = (prevProps, nextProps) => nextProps.isLoading === prevProps.isLoading

PostForm.propTypes = {
  post: PropTypes.object,
  default_language: PropTypes.string,
  blogUrl: PropTypes.string,
  isLoading: PropTypes.bool,
  languages: PropTypes.array,
  isCreatePost: PropTypes.bool,
  onChange: PropTypes.func,
  setErrorForm: PropTypes.func,
};

export default memo(PostForm, arePropsEqual)
